import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/lib/api'
import type { WorkspaceUsage, WorkspaceWithRole, WorkspaceMember, FormWithList, FormSettings, FormDetailConfig, SheetWithList, CreateSheetRequest, UpdateSheetRequest, Chart, CreateChartRequest, UpdateChartRequest } from '@sevfy/shared'
import { useLayoutStore } from '@/stores/layout'
import { useDashboardPages } from '@/composables/useDashboardPages'

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000

const WORKSPACE_ID_KEY = 'sevfy.activeWorkspaceId'

export const useFormsStore = defineStore('forms', () => {
  const forms = ref<FormWithList[]>([])
  const lastFetched = ref<number | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Check if cache is still valid
  const isCacheValid = computed(() => {
    if (!lastFetched.value) return false
    return Date.now() - lastFetched.value < CACHE_DURATION
  })

  // Get form by ID or shortId
  function getFormById(id: string): FormWithList | undefined {
    return forms.value.find(form => form.id === id || form.shortId === id)
  }

  // Get form by slug (publicToken)
  function getFormByToken(token: string): FormWithList | undefined {
    return forms.value.find(form => form.publicToken === token)
  }

  // Total submissions across all forms
  const totalSubmissions = computed(() => {
    return forms.value.reduce((sum, form) => sum + (form.submissionCount || 0), 0)
  })

  // Fetch all forms (with caching)
  async function fetchForms(force = false): Promise<FormWithList[]> {
    // Return cached data if valid and not forcing refresh
    if (!force && isCacheValid.value && forms.value.length > 0) {
      return forms.value
    }

    isLoading.value = true
    try {
      const data = await api.get<FormWithList[]>('/forms')
      forms.value = data
      lastFetched.value = Date.now()
      return data
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch forms'
      // Return empty array instead of throwing for optional data
      return []
    } finally {
      isLoading.value = false
    }
  }

  // Fetch a single form
  async function fetchForm(id: string, force = false): Promise<FormWithList> {
    // Check cache first
    if (!force) {
      const cached = getFormById(id)
      if (cached && isCacheValid.value) {
        return cached
      }
    }
    try {
      const data = await api.get<FormWithList>(`/forms/${id}`)
      // Use data.id (always a CUID) so shortId route params don't cause duplicates
      const index = forms.value.findIndex(f => f.id === data.id)
      if (index >= 0) {
        forms.value[index] = data
      } else {
        forms.value.push(data)
      }
      return data
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch form'
      throw e
    }
  }

  // Create form input type
  interface CreateFormInput {
    name: string
    listDefinitionId: string
    settings?: FormSettings
    detailConfig?: FormDetailConfig[]
    isActive?: boolean
  }

async function createForm(data: CreateFormInput): Promise<FormWithList> {
  try {
    const newForm = await api.post<FormWithList>('/forms', data)
    forms.value.push(newForm)
    return newForm
  } catch (e: any) {
    error.value = e.message || 'Failed to create form'
    throw e
  }
}

async function updateForm(id: string, data: Partial<CreateFormInput>): Promise<FormWithList> {
  try {
    const updated = await api.patch<FormWithList>(`/forms/${id}`, data)
    const index = forms.value.findIndex(f => f.id === updated.id)
    if (index >= 0) {
      forms.value[index] = updated
    }
    return updated
  } catch (e: any) {
    error.value = e.message || 'Failed to update form'
    throw e
  }
}

async function deleteForm(id: string): Promise<void> {
  try {
    await api.delete(`/forms/${id}`)
    forms.value = forms.value.filter(f => f.id !== id)
  } catch (e: any) {
    error.value = e.message || 'Failed to delete form'
    throw e
  }
}

  // Invalidate cache
  function invalidateCache() {
    lastFetched.value = null
  }

  // Clear all data
  function $reset() {
    forms.value = []
    lastFetched.value = null
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    forms,
    lastFetched,
    isLoading,
    error,

    // Getters
    isCacheValid,
    totalSubmissions,
    getFormById,
    getFormByToken,

    // Actions
    fetchForms,
    fetchForm,
    createForm,
    updateForm,
    deleteForm,
    invalidateCache,
    $reset
  }
})

// =============================================================================
// Sheets Store (renamed from Public Views Store)
// =============================================================================

export const useSheetsStore = defineStore('sheets', () => {
  const sheets = ref<SheetWithList[]>([])
  const lastFetched = ref<number | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isCacheValid = computed(() => {
    if (!lastFetched.value) return false
    return Date.now() - lastFetched.value < CACHE_DURATION
  })

  // Get sheet by ID or shortId
  function getSheetById(id: string): SheetWithList | undefined {
    return sheets.value.find(s => s.id === id || s.shortId === id)
  }

  async function fetchSheets(force = false): Promise<SheetWithList[]> {
    if (!force && isCacheValid.value && sheets.value.length > 0) {
      return sheets.value
    }
    isLoading.value = true
    try {
      const data = await api.get<SheetWithList[]>('/sheets')
      sheets.value = data
      lastFetched.value = Date.now()
      return data
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch sheets'
      return []
    } finally {
      isLoading.value = false
    }
  }

  async function fetchSheet(id: string, force = false): Promise<SheetWithList> {
    if (!force) {
      const cached = getSheetById(id)
      if (cached && isCacheValid.value) return cached
    }
    try {
      const data = await api.get<SheetWithList>(`/sheets/${id}`)
      // Use data.id (always a CUID) so shortId route params don't cause duplicates
      const index = sheets.value.findIndex(s => s.id === data.id)
      if (index >= 0) {
        sheets.value[index] = data
      } else {
        sheets.value.push(data)
      }
      return data
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch sheet'
      throw e
    }
  }

  async function createSheet(data: CreateSheetRequest): Promise<SheetWithList> {
    try {
      const newSheet = await api.post<SheetWithList>('/sheets', data)
      sheets.value.push(newSheet)
      return newSheet
    } catch (e: any) {
      error.value = e.message || 'Failed to create sheet'
      throw e
    }
  }

  async function updateSheet(id: string, data: UpdateSheetRequest): Promise<SheetWithList> {
    try {
      const updated = await api.patch<SheetWithList>(`/sheets/${id}`, data)
      const index = sheets.value.findIndex(s => s.id === updated.id)
      if (index >= 0) sheets.value[index] = updated
      return updated
    } catch (e: any) {
      error.value = e.message || 'Failed to update sheet'
      throw e
    }
  }

  async function deleteSheet(id: string): Promise<void> {
    try {
      await api.delete(`/sheets/${id}`)
      sheets.value = sheets.value.filter(s => s.id !== id)
    } catch (e: any) {
      error.value = e.message || 'Failed to delete sheet'
      throw e
    }
  }

  async function regenerateToken(id: string): Promise<string> {
    try {
      const result = await api.post<{ publicToken: string }>(`/sheets/${id}/regenerate-token`, {})
      const index = sheets.value.findIndex(s => s.id === id)
      if (index >= 0) sheets.value[index] = { ...sheets.value[index], publicToken: result.publicToken }
      return result.publicToken
    } catch (e: any) {
      error.value = e.message || 'Failed to regenerate token'
      throw e
    }
  }

  async function setPassword(id: string, password: string | null): Promise<void> {
    try {
      const result = await api.post<{ hasPassword: boolean }>(`/sheets/${id}/set-password`, { password })
      const index = sheets.value.findIndex(s => s.id === id)
      if (index >= 0) sheets.value[index] = { ...sheets.value[index], hasPassword: result.hasPassword }
    } catch (e: any) {
      error.value = e.message || 'Failed to set password'
      throw e
    }
  }

  function invalidateCache() {
    lastFetched.value = null
  }

  function $reset() {
    sheets.value = []
    lastFetched.value = null
    isLoading.value = false
    error.value = null
  }

  return {
    sheets, lastFetched, isLoading, error,
    isCacheValid, getSheetById,
    fetchSheets, fetchSheet, createSheet, updateSheet, deleteSheet,
    regenerateToken, setPassword, invalidateCache, $reset,
  }
})

// =============================================================================
// Charts Store
// =============================================================================

export const useChartsStore = defineStore('charts', () => {
  const charts = ref<Chart[]>([])
  const lastFetched = ref<number | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isCacheValid = computed(() => {
    if (!lastFetched.value) return false
    return Date.now() - lastFetched.value < CACHE_DURATION
  })

  // Get chart by ID or shortId
  function getChartById(id: string): Chart | undefined {
    return charts.value.find(c => c.id === id || c.shortId === id)
  }

  async function fetchCharts(force = false): Promise<Chart[]> {
    if (!force && isCacheValid.value && charts.value.length > 0) {
      return charts.value
    }
    isLoading.value = true
    try {
      const data = await api.get<Chart[]>('/charts')
      charts.value = data
      lastFetched.value = Date.now()
      return data
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch charts'
      return []
    } finally {
      isLoading.value = false
    }
  }

  async function fetchChart(id: string, force = false): Promise<Chart> {
    if (!force) {
      const cached = getChartById(id)
      if (cached && isCacheValid.value) return cached
    }
    try {
      const data = await api.get<Chart>(`/charts/${id}`)
      // Use data.id (always a CUID) so shortId route params don't cause duplicates
      const index = charts.value.findIndex(c => c.id === data.id)
      if (index >= 0) {
        charts.value[index] = data
      } else {
        charts.value.push(data)
      }
      return data
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch chart'
      throw e
    }
  }

  async function createChart(data: CreateChartRequest): Promise<Chart> {
    try {
      const newChart = await api.post<Chart>('/charts', data)
      charts.value.push(newChart)
      return newChart
    } catch (e: any) {
      error.value = e.message || 'Failed to create chart'
      throw e
    }
  }

  async function updateChart(id: string, data: UpdateChartRequest): Promise<Chart> {
    try {
      const updated = await api.patch<Chart>(`/charts/${id}`, data)
      const index = charts.value.findIndex(c => c.id === updated.id)
      if (index >= 0) charts.value[index] = updated
      return updated
    } catch (e: any) {
      error.value = e.message || 'Failed to update chart'
      throw e
    }
  }

  async function deleteChart(id: string): Promise<void> {
    try {
      await api.delete(`/charts/${id}`)
      charts.value = charts.value.filter(c => c.id !== id)
    } catch (e: any) {
      error.value = e.message || 'Failed to delete chart'
      throw e
    }
  }

  async function setPassword(id: string, password: string | null): Promise<void> {
    try {
      const result = await api.post<{ hasPassword: boolean }>(`/charts/${id}/set-password`, { password })
      const index = charts.value.findIndex(c => c.id === id)
      if (index >= 0) charts.value[index] = { ...charts.value[index], hasPassword: result.hasPassword }
    } catch (e: any) {
      error.value = e.message || 'Failed to set password'
      throw e
    }
  }

  async function regenerateToken(id: string): Promise<string> {
    try {
      const result = await api.post<{ publicToken: string }>(`/charts/${id}/regenerate-token`, {})
      const index = charts.value.findIndex(c => c.id === id)
      if (index >= 0) charts.value[index] = { ...charts.value[index], publicToken: result.publicToken }
      return result.publicToken
    } catch (e: any) {
      error.value = e.message || 'Failed to regenerate token'
      throw e
    }
  }

  function invalidateCache() {
    lastFetched.value = null
  }

  function $reset() {
    charts.value = []
    lastFetched.value = null
    isLoading.value = false
    error.value = null
  }

  return {
    charts, lastFetched, isLoading, error,
    isCacheValid, getChartById,
    fetchCharts, fetchChart, createChart, updateChart, deleteChart,
    setPassword, regenerateToken, invalidateCache, $reset,
  }
})

// Separate store for workspace/usage data (extends auth store concept)
export const useWorkspaceStore = defineStore('workspace', () => {
  const currentWorkspace = ref<WorkspaceWithRole | null>(null)
  const workspaces = ref<WorkspaceWithRole[]>([])
  const usage = ref<WorkspaceUsage | null>(null)
  const lastUsageFetch = ref<number | null>(null)
  const lastWorkspaceFetch = ref<number | null>(null)
  // Per-workspace details for WorkspacesView - cached across remounts
  const workspaceUsage = ref<Record<string, WorkspaceUsage>>({})
  const workspaceMembers = ref<Record<string, WorkspaceMember[]>>({})
  const isLoading = ref(false)
  const error = ref<string | null>(null)

    // Load workspaces and set current (cached - skips fetch if workspaces are fresh)
    async function loadWorkspaces(force = false): Promise<WorkspaceWithRole[]> {
      // Skip if we've already fetched (even if the user has no workspaces yet)
      if (!force && lastWorkspaceFetch.value !== null) {
        return workspaces.value
      }

      isLoading.value = true
      error.value = null

      try {
        workspaces.value = await api.get<WorkspaceWithRole[]>('/auth/tenants')
        lastWorkspaceFetch.value = Date.now()
        // Restore workspace from localStorage if present
        const storedId = localStorage.getItem(WORKSPACE_ID_KEY)
        if (storedId) {
          const ws = workspaces.value.find(w => w.id === storedId)
          if (ws) {
            currentWorkspace.value = ws
            api.setWorkspaceId(ws.id)
          } else {
            // Stale ID from a different user or deleted workspace - clear it
            localStorage.removeItem(WORKSPACE_ID_KEY)
          }
        }
        if (!currentWorkspace.value && workspaces.value.length > 0) {
          currentWorkspace.value = workspaces.value[0]
          api.setWorkspaceId(currentWorkspace.value.id)
          localStorage.setItem(WORKSPACE_ID_KEY, currentWorkspace.value.id)
        }
        return workspaces.value
      } catch (e: any) {
        error.value = e.message || 'Failed to load workspaces'
        throw e
      } finally {
        isLoading.value = false
      }
    }

  // Usage cache duration (3 minutes - usage changes frequently)
  const USAGE_CACHE_DURATION = 3 * 60 * 1000

  const isUsageCacheValid = computed(() => {
    if (!lastUsageFetch.value) return false
    return Date.now() - lastUsageFetch.value < USAGE_CACHE_DURATION
  })

  // Switch to a different workspace and persist
  function switchWorkspace(workspaceId: string) {
    const ws = workspaces.value.find(w => w.id === workspaceId)
    if (ws) {
      currentWorkspace.value = ws
      api.setWorkspaceId(ws.id)
      // Set the new workspace ID first - layout store's storageKey() reads this
      localStorage.setItem(WORKSPACE_ID_KEY, ws.id)
      // Invalidate usage cache when switching workspaces
      lastUsageFetch.value = null
      usage.value = null
      // Reset layout state so the new workspace starts clean (no stale widgets/pages)
      useLayoutStore().resetForWorkspace()
      useDashboardPages().init(ws.id)
    }
  }

  // Fetch usage data (with caching)
  async function fetchUsage(force = false): Promise<WorkspaceUsage | null> {
    if (!currentWorkspace.value) {
      return null
    }

    // Return cached data if valid
    if (!force && isUsageCacheValid.value && usage.value) {
      return usage.value
    }

    try {
      usage.value = await api.get<WorkspaceUsage>('/auth/usage')
      lastUsageFetch.value = Date.now()
      return usage.value
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch usage'
      throw e
    }
  }

  // Invalidate usage cache (call after creating/deleting entries)
  function invalidateUsage() {
    lastUsageFetch.value = null
  }

  // Load usage + members for all workspaces in parallel, skipping any that
  // are already cached. Pass force=true to refresh all (e.g. after mutations).
  async function loadWorkspaceDetails(workspaceList: WorkspaceWithRole[], force = false): Promise<void> {
    const toFetch = force
      ? workspaceList
      : workspaceList.filter(w => !workspaceUsage.value[w.id])

    if (toFetch.length === 0) return

    await Promise.all(toFetch.map(async (workspace) => {
      try {
        const [usageData, membersData] = await Promise.all([
          api.get<WorkspaceUsage>('/auth/usage', { workspaceId: workspace.id }),
          api.get<WorkspaceMember[]>(`/auth/tenants/${workspace.id}/members`, { workspaceId: workspace.id }),
        ])
        workspaceUsage.value[workspace.id] = usageData
        workspaceMembers.value[workspace.id] = membersData
      } catch {
        // Non-fatal - leave previous data in place
      }
    }))
  }

  function invalidateWorkspaceDetails() {
    workspaceUsage.value = {}
    workspaceMembers.value = {}
  }

  // Update the terminology for the current workspace and persist to the store.
  // The API call is made by the caller (settings UI); this just keeps the
  // in-memory workspace in sync so useTerminology() reacts immediately.
  function updateCurrentTerminology(terminology: import('@sevfy/shared').TerminologyMap | null) {
    if (!currentWorkspace.value) return
    currentWorkspace.value = { ...currentWorkspace.value, terminology }
    const idx = workspaces.value.findIndex(w => w.id === currentWorkspace.value!.id)
    if (idx >= 0) {
      workspaces.value[idx] = { ...workspaces.value[idx], terminology }
    }
  }

  // Clear all data
  function $reset() {
    currentWorkspace.value = null
    workspaces.value = []
    usage.value = null
    lastUsageFetch.value = null
    lastWorkspaceFetch.value = null
    workspaceUsage.value = {}
    workspaceMembers.value = {}
    isLoading.value = false
    error.value = null
    // Clear the stale workspace ID from the API client so the next user's
    // requests don't send a stale workspace header they have no access to
    api.setWorkspaceId(null)
  }

  return {
    // State
    currentWorkspace,
    workspaces,
    usage,
    workspaceUsage,
    workspaceMembers,
    isLoading,
    error,

    // Getters
    isUsageCacheValid,

    // Actions
    loadWorkspaces,
    switchWorkspace,
    fetchUsage,
    invalidateUsage,
    loadWorkspaceDetails,
    invalidateWorkspaceDetails,
    updateCurrentTerminology,
    $reset
  }
})
